"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.summarizeRealtimeScoringSemanticComparisons = exports.compareRealtimeScoringSemanticSnapshot = exports.selectRealtimeScoringSemanticFieldMappings = exports.realtimeScoringSemanticFieldMappings = void 0;
/**
 * Fields that can be inspected while migrating a structured legacy filter.
 * This is diagnostic source comparison, not a claim that master-view and raw
 * source semantics are equivalent.
 */
exports.realtimeScoringSemanticFieldMappings = [
    {
        legacyField: 'iso3166',
        metric: 'company.country.iso3166',
        legacyPath: 'filters.iso3166',
    },
    {
        legacyField: 'numberOfEmployees',
        metric: 'workforce.employees.current',
        legacyPath: 'filters.numberOfEmployees',
    },
    {
        legacyField: 'operationalStatus',
        metric: 'company.operational_status',
        legacyPath: 'filters.operationalStatus',
    },
    {
        legacyField: 'netSales',
        metric: 'financial.revenue.current',
        legacyPath: 'filters.netSales',
    },
    {
        legacyField: 'sniClassification',
        metric: 'company.industry.sni_classification',
        legacyPath: 'filters.sniClassification',
    },
];
const metricsUsedByCondition = (condition) => {
    switch (condition.kind) {
        case 'predicate':
            return [condition.metric];
        case 'ratio':
            return [condition.numeratorMetric, condition.denominatorMetric];
        case 'all':
        case 'any':
            return condition.conditions.flatMap(metricsUsedByCondition);
        case 'not':
            return metricsUsedByCondition(condition.condition);
    }
};
/**
 * Limits source-semantic diagnostics to facts that participate in both sides
 * of the migration. This keeps unrelated legacy fields from being reported as
 * missing merely because a V1 definition does not use them.
 */
const selectRealtimeScoringSemanticFieldMappings = (configuration, definition, mappings = exports.realtimeScoringSemanticFieldMappings) => {
    if (configuration?.filters === undefined || definition === undefined) {
        return [];
    }
    const metrics = new Set([
        ...metricsUsedByCondition(definition.eligibility),
        ...definition.scoreRules.flatMap((rule) => metricsUsedByCondition(rule.when)),
    ]);
    return mappings.filter((mapping) => configuration.filters?.[mapping.legacyField] !== undefined &&
        metrics.has(mapping.metric));
};
exports.selectRealtimeScoringSemanticFieldMappings = selectRealtimeScoringSemanticFieldMappings;
const isMoneyValue = (value) => typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'amount' in value &&
    typeof value.amount === 'number' &&
    'currency' in value &&
    typeof value.currency === 'string';
const asComparableValue = (value) => value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? value
    : isMoneyValue(value)
        ? value
        : undefined;
const valuesMatch = (legacyValue, realtimeValue) => {
    if (isMoneyValue(legacyValue) && isMoneyValue(realtimeValue)) {
        return legacyValue.amount === realtimeValue.amount && legacyValue.currency === realtimeValue.currency;
    }
    return legacyValue === realtimeValue;
};
/**
 * Compares only mapped values with an explicit normalisation policy for the
 * same organisation number. Lists and ratios remain not comparable.
 */
const compareRealtimeScoringSemanticSnapshot = (snapshot, mappings = exports.realtimeScoringSemanticFieldMappings) => mappings.map((mapping) => {
    const legacyValue = snapshot.legacyValues[mapping.legacyField];
    const realtimeValue = asComparableValue(snapshot.realtimeMetricValues[mapping.metric]);
    if (legacyValue === undefined || realtimeValue === undefined || legacyValue === null || realtimeValue === null) {
        return {
            orgnr: snapshot.orgnr,
            legacyPath: mapping.legacyPath,
            metric: mapping.metric,
            status: 'not_comparable',
            legacyValue,
            realtimeValue,
            ...(mapping.metric === 'workforce.employees.current' && snapshot.realtimeWorkforceEvidence !== undefined
                ? { realtimeWorkforceEvidence: snapshot.realtimeWorkforceEvidence }
                : {}),
            reason: 'One source value is missing or does not yet have a comparable normalization policy.',
        };
    }
    return {
        orgnr: snapshot.orgnr,
        legacyPath: mapping.legacyPath,
        metric: mapping.metric,
        status: valuesMatch(legacyValue, realtimeValue) ? 'match' : 'value_mismatch',
        legacyValue,
        realtimeValue,
        ...(mapping.metric === 'workforce.employees.current' && snapshot.realtimeWorkforceEvidence !== undefined
            ? { realtimeWorkforceEvidence: snapshot.realtimeWorkforceEvidence }
            : {}),
    };
});
exports.compareRealtimeScoringSemanticSnapshot = compareRealtimeScoringSemanticSnapshot;
const summarizeRealtimeScoringSemanticComparisons = (comparisons) => comparisons.reduce((summary, comparison) => comparison.status === 'match'
    ? { ...summary, matchCount: summary.matchCount + 1 }
    : comparison.status === 'value_mismatch'
        ? { ...summary, valueMismatchCount: summary.valueMismatchCount + 1 }
        : { ...summary, notComparableCount: summary.notComparableCount + 1 }, { matchCount: 0, valueMismatchCount: 0, notComparableCount: 0 });
exports.summarizeRealtimeScoringSemanticComparisons = summarizeRealtimeScoringSemanticComparisons;
//# sourceMappingURL=semantic-comparator.js.map